import { useEffect, useState } from "react";
import { Offcanvas } from "react-bootstrap";
import AddProviderForm, { AddProviderFormValues } from "./AddProviderForm";

interface AddProviderModalProps {
  show: boolean;
  onClose: () => void;
  onSubmit: (values: AddProviderFormValues) => Promise<void> | void;
  allowDismiss?: boolean;
}

const AddProviderModal = ({
  show,
  onClose,
  onSubmit,
  allowDismiss = true,
}: AddProviderModalProps) => {
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  useEffect(() => {
    if (!show) {
      setSaving(false);
      setSaveError("");
    }
  }, [show]);

  const handleClose = () => {
    if (!allowDismiss || saving) return;
    setSaveError("");
    onClose();
  };

  const handleSubmit = async (values: AddProviderFormValues) => {
    if (saving) return;
    setSaving(true);
    setSaveError("");
    try {
      await onSubmit(values);
      setSaveError("");
    } catch {
      setSaveError("Unable to add provider. Check the code is unique and try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Offcanvas
      show={show}
      onHide={handleClose}
      placement="end"
      backdrop={allowDismiss}
      keyboard={allowDismiss}
      className="ic-provider-form-offcanvas"
    >
      <Offcanvas.Header closeButton={allowDismiss}>
        <Offcanvas.Title>Add Provider</Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        {show && (
          <>
            {saveError && (
              <div
                className="ic-inline-message ic-inline-message-error mb-3"
                role="alert"
              >
                {saveError}
              </div>
            )}
            <AddProviderForm
              mode="add"
              isSubmitting={saving}
              cancelDisabled={!allowDismiss}
              onCancel={handleClose}
              onSubmit={handleSubmit}
            />
          </>
        )}
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default AddProviderModal;
